
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { KnowledgeEntry, SearchResult, SearchOptions } from "@/components/knowledge/types";

export function useKnowledgeSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const preprocessSearchQuery = (query: string): string => {
    return query.trim().toLowerCase();
  };

  const handleSearch = useCallback(async (searchQuery: string, options?: SearchOptions) => {
    const processedQuery = preprocessSearchQuery(searchQuery);
    if (!processedQuery) {
      queryClient.invalidateQueries({ queryKey: ['knowledgeEntries'] });
      return [];
    }
    
    setIsSearching(true);
    try {
      console.log('Searching for:', processedQuery, 'with options:', options);

      // Use the knowledge-search edge function
      const { data, error } = await supabase.functions.invoke(
        'knowledge-search',
        {
          body: { 
            query: processedQuery, 
            limit: options?.limit || 10,
            includeContent: options?.includeContent !== false
          },
        }
      );

      if (error) {
        console.error('Search error:', error);
        throw error;
      }

      if (!data?.results) {
        throw new Error('No search results returned');
      }

      const searchResults = data.results;
      console.log('Search results:', searchResults);
      
      // Transform search results to match expected format
      const transformedResults: SearchResult[] = searchResults.map(result => ({
        ...result,
        embedding_status: 'completed' as const,
        embedding: null,
        embedding_error: null,
        embedding_updated_at: null,
        metadata: result.metadata || {},
        created_by: null,
        updated_at: null,
        file_type: result.file_type || null
      }));

      queryClient.setQueryData(['knowledgeEntries'], transformedResults);
      
      return transformedResults;
    } catch (error) {
      console.error('Search error:', error);
      toast({
        variant: "destructive",
        title: "Search Error",
        description: "Failed to search knowledge entries",
      });
      return [];
    } finally {
      setIsSearching(false);
    }
  }, [queryClient, toast]);

  return {
    isSearching,
    handleSearch
  };
}
